import React, { useState, useMemo } from 'react'
import { FeaturedLayout } from '../../layouts'
import { ChipFilter, Label } from '../../ui'
import { formatDate } from '../../../utils/helpers'
import { useDeportes } from '../../../hooks/useDeportes'
import { etiquetaTemporada } from '../../../lib/uniformesMultiTemporada'
import { useEntregasData } from './entrega/useEntregasData'
import ListaEntregas from './entrega/ListaEntregas'
import EstacionEntrega from './entrega/EstacionEntrega'
import TableroAvance from './entrega/TableroAvance'

// Paso 6 del ciclo: llego la caja y se reparte.
//
// Junta las tres pantallas que miraban lo mismo desde distinto angulo:
//
//   Entregas          -> vista "Lista": un renglon por pieza, con filtros y Excel
//   Tallas y entrega  -> vista "Por prenda": el mostrador, una caja a la vez
//   Tablero           -> el avance, que era una pantalla a la que habia que ir y
//                        volver; ahora vive encima de la lista que explica
const VISTAS = [
  { value: 'lista', label: 'Lista' },
  { value: 'prenda', label: 'Por prenda' },
  { value: 'avance', label: 'Avance' },
]

const RepartoPage = () => {
  const [vista, setVista] = useState('lista')
  // Tocar una prenda en el avance lleva al mostrador YA con esa prenda puesta, que es
  // lo que hacia el Tablero navegando a otra pantalla.
  const [prendaElegida, setPrendaElegida] = useState('')
  const {
    temporadas, temporadaId, setTemporadaId, matriz, grupos, orgDeportes, staffById,
    loading, reload, inscritosEstado, entregasDelPadron,
  } = useEntregasData()

  // Repartir es por CAJA, y una caja es un pedido. Sin este filtro la pantalla
  // mostraba las piezas de toda la temporada revueltas -- las de esta caja, las de
  // otro pedido que aun no llega, las que nunca se pidieron y las ya entregadas -- y
  // habia que adivinar cuales venian en la que tienes enfrente.
  const [pedidoSel, setPedidoSel] = useState('todos')
  const { label: deporteLabel } = useDeportes()

  // La temporada vive AQUI, una sola vez para las tres vistas. Antes la Lista traia
  // su propio desplegable y el mostrador sus chips: dos controles distintos para lo
  // mismo, en la misma pantalla.
  const chipsTemporada = useMemo(
    () => (temporadas || [])
      .filter((t) => t.estado === 'abierta' || t.id === temporadaId)
      .map((t) => ({ value: t.id, label: etiquetaTemporada(t, deporteLabel) })),
    [temporadas, temporadaId, deporteLabel],
  )

  const chipsPedido = useMemo(() => {
    const vivos = (matriz.pedidos || []).filter((p) => p.estado !== 'cancelado')
    if (!vivos.length) return []
    return [
      { value: 'todos', label: 'Todos' },
      ...vivos
        .sort((a, b) => String(b.fecha_pedido || '').localeCompare(String(a.fecha_pedido || '')))
        .map((p) => ({
          value: p.id,
          label: `${p.fecha_pedido ? formatDate(p.fecha_pedido) : 'Sin fecha'} · ${p.proveedor || 'Sin proveedor'}`,
        })),
    ]
  }, [matriz.pedidos])

  // Sin talla la pieza NO EXISTE todavia (`jugadores_prendas.talla` es obligatorio):
  // es necesidad calculada, no un registro. No se puede repartir lo que no se ha
  // pedido ni fabricado, asi que esas filas solo metian ruido en el mostrador. Su
  // lugar es la pantalla de Tallas.
  const entregasFiltradas = useMemo(() => {
    const conTalla = (entregasDelPadron || []).filter((e) => e.talla)
    return pedidoSel === 'todos' ? conTalla : conTalla.filter((e) => e.pedido_id === pedidoSel)
  }, [entregasDelPadron, pedidoSel])

  return (
    <FeaturedLayout
      title="Reparto"
      subtitle="Entrega lo que ya llegó del proveedor"
      refreshAction={reload}
    >
      {chipsTemporada.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Temporada</Label>
          <ChipFilter
            ariaLabel="Temporada"
            scroll
            opciones={chipsTemporada}
            valor={temporadaId || ''}
            onChange={(v) => v && setTemporadaId(v)}
          />
        </div>
      )}

      {chipsPedido.length > 1 && (
        <div className="mb-3 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Pedido</Label>
          <ChipFilter
            ariaLabel="Pedido"
            scroll
            opciones={chipsPedido}
            valor={pedidoSel}
            onChange={(v) => v && setPedidoSel(v)}
          />
        </div>
      )}

      <div className="mb-3 space-y-1.5">
        <Label className="text-xs text-muted-foreground">Vista</Label>
        <ChipFilter
          ariaLabel="Vista"
          opciones={VISTAS}
          valor={vista}
          onChange={(v) => v && setVista(v)}
        />
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : vista === 'avance' ? (
        <TableroAvance
          matriz={matriz}
          grupos={grupos}
          orgDeportes={orgDeportes}
          entregasDelPadron={entregasFiltradas}
          onRegistrarPrenda={(prenda) => { setVista('prenda'); setPrendaElegida(prenda.id) }}
        />
      ) : vista === 'prenda' ? (
        <EstacionEntrega
          matriz={matriz}
          grupos={grupos}
          prendaIdInicial={prendaElegida}
          conTemporada={false}
          entregasDelPadron={entregasFiltradas}
          onChanged={reload}
        />
      ) : (
        <ListaEntregas
          matriz={matriz}
          grupos={grupos}
          orgDeportes={orgDeportes}
          staffById={staffById}
          inscritosEstado={inscritosEstado}
          entregasDelPadron={entregasFiltradas}
          onChanged={reload}
          loading={loading}
        />
      )}
    </FeaturedLayout>
  )
}

export default RepartoPage
